// Domain-owned route containers. Do not import legacy connected-container or all-domain registries here.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { EvidenceItem, WorkflowCommand } from "@swot/swot-components";
import { readCmtsSession } from "../../auth/authStore.js";
import { resolveCurrentCmtsSession } from "../../session/cmtsSessionResolver.js";
import { useCmtsDispatch } from "@store/hooks.js";
import { authActions } from "@store/authSlice.js";
import { permissionActions } from "@store/permissionSlice.js";
import { tenantActions } from "@store/tenantSlice.js";
import { serializeReduxError } from "@store/thunkExtra.js";
import { useCmtsAuth } from "../../auth/authHooks.js";
import { useComplaintRegister } from "@hooks/useComplaintRegister.js";
import { useComplaintDetail } from "@hooks/useComplaintDetail.js";
import { useComplaintWorkflow } from "@hooks/useComplaintWorkflow.js";
import { useEvidence } from "@hooks/useEvidence.js";
import { cmtsKeys } from "@hooks/cmtsQueryKeys.js";
import type { RepositoryResult } from "../../repositories/repositoryResult.js";
import type { CmtsHostRuntime } from "../../../../app/appRuntime.js";
import type {
  CmtsComplaintDetailVm,
  CmtsComplaintRecordVm,
} from "../../view-models/complaintViewModels.js";
import type {
  CmtsConfigurationDraftVm,
  CmtsConfigurationVersionVm,
} from "../../mappers/configurationMappers.js";
import { queryObjectFromSearch } from "../../filters/cmtsFilterEngine.js";
import {
  decideCmtsExport,
  createCmtsExportAuditPayload,
} from "../../export/cmtsSecureExport.js";
import { getCmtsPersonaLandingPath } from "../../routes/cmtsPersonaLanding.js";
import {
  normalizeTypedReportCode,
  reportCodeFromPathname,
} from "../../reporting/cmtsReportConfig.js";
import { throwCmtsCanonicalError } from "../../api/cmtsApiErrors.js";
import { normalizeUserSafeApiError } from "../../api/apiErrorNormalizer.js";
import { cmtsMasterDataEntityForPathSegment } from "../../master-data/cmtsEnterpriseMasterDataRegistry.js";
import { requireCmtsGuid } from "../../utils/cmtsGuid.js";
import {
  activeRows,
  assertRepositoryOk,
  businessValueOptionOf,
  complaintQuery,
  createOrUpdateMasterData,
  dataOr,
  deactivateMasterData,
  diagnosticsOf,
  emptyBackendDashboard,
  errorOf,
  invalidateAdminArea,
  loadMasterOptions,
  masterCrudDisplayName,
  optionOf,
  reactivateMasterData,
  tenantId,
  useAdminDependencyData,
  useCmtsRouterSearch,
  type CmtsConnectedContainerProps,
} from "../domainContainerSupport.js";
import { CmtsSodAdminPage } from "@modules/pages/cmtsSodAdminPage.js";
import { CmtsMasterDependencyPage } from "@modules/pages/cmtsMasterDependencyPage.js";
import { CmtsAdminCalendarSchedulerPage } from "@modules/pages/cmtsAdminCalendarSchedulerPage.js";
import { CmtsScheduledJobsPage } from "@modules/pages/cmtsScheduledJobsPage.js";

export function CmtsAdminCalendarSchedulerContainer({
  runtime,
}: CmtsConnectedContainerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const key = ["cmts", "calendar", t];
  const query = useQuery({
    queryKey: key,
    queryFn: () => runtime.repositories.calendar.profiles(t),
    retry: 1,
  });
  const holidayKey = ["cmts", "calendar", "holidays", t];
  const shiftKey = ["cmts", "calendar", "shifts", t];
  const holidays = useQuery({
    queryKey: holidayKey,
    queryFn: () => runtime.repositories.calendar.holidays(t),
    retry: 1,
  });
  const shifts = useQuery({
    queryKey: shiftKey,
    queryFn: () => runtime.repositories.calendar.shifts(t),
    retry: 1,
  });
  const eventTypeOptions = useQuery({
    queryKey: ["cmts", "master-options", "calendar-event-type", t],
    queryFn: () =>
      loadMasterOptions(
        runtime,
        "calendar-event-type",
        t,
        "calendar-event-type",
      ),
    retry: 1,
  });
  const priorityOptions = useQuery({
    queryKey: ["cmts", "master-options", "calendar-priority", t],
    queryFn: () => loadMasterOptions(runtime, "priority", t, "priority"),
    retry: 1,
  });

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      runtime.repositories.calendar.create(
        {
          tenantId: t,
          code: payload.code ?? payload.calendarCode,
          name: payload.name ?? payload.calendarName,
          calendarType: payload.calendarType ?? "BUSINESS",
          timeZone: payload.timeZone ?? payload.timeZoneId ?? "Asia/Kolkata",
          isDefault: payload.isDefault ?? false,
          isActive: payload.isActive ?? true,
          effectiveFrom: payload.effectiveFrom,
          effectiveTo: payload.effectiveTo,
          workingDaysJson: payload.workingDaysJson,
          workingHoursJson: payload.workingHoursJson,
          rowVersion: payload.rowVersion,
        },
        t,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
    },
  });
  const reschedule = useMutation({
    mutationFn: ({
      row,
      payload,
    }: {
      row: Record<string, unknown>;
      payload: Record<string, unknown>;
    }) =>
      runtime.repositories.calendar.reschedule(
        String(row.calendarCode ?? row.code ?? ""),
        payload,
        t,
      ),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: key }),
  });
  const cancel = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.calendar.cancel(
        String(row.calendarCode ?? row.code ?? ""),
        t,
      ),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: key }),
  });
  const dueDate = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      runtime.repositories.calendar.calculateDueDate(
        { ...payload, tenantId: t },
        t,
      ),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: key }),
  });
  const createHoliday = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      runtime.repositories.calendar.createHoliday(payload, t),
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: holidayKey }),
  });
  const updateHoliday = useMutation({
    mutationFn: ({
      row,
      payload,
    }: {
      row: Record<string, unknown>;
      payload: Record<string, unknown>;
    }) =>
      runtime.repositories.calendar.updateHoliday(
        requireCmtsGuid(row.id, "Holiday id"),
        payload,
        t,
      ),
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: holidayKey }),
  });
  const deactivateHoliday = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.calendar.deactivateHoliday(
        requireCmtsGuid(row.id, "Holiday id"),
        t,
      ),
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: holidayKey }),
  });
  const createShift = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      runtime.repositories.calendar.createShift(payload, t),
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: shiftKey }),
  });
  const updateShift = useMutation({
    mutationFn: ({
      row,
      payload,
    }: {
      row: Record<string, unknown>;
      payload: Record<string, unknown>;
    }) =>
      runtime.repositories.calendar.updateShift(
        requireCmtsGuid(row.id, "Shift id"),
        payload,
        t,
      ),
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: shiftKey }),
  });
  const deactivateShift = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.calendar.deactivateShift(
        requireCmtsGuid(row.id, "Shift id"),
        t,
      ),
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: shiftKey }),
  });
  const rows = dataOr(query.data, []) as readonly Record<string, unknown>[];
  const apiError = firstUserSafeMessage(
    query.error,
    holidays.error,
    shifts.error,
    create.error,
    reschedule.error,
    cancel.error,
    dueDate.error,
    createHoliday.error,
    updateHoliday.error,
    deactivateHoliday.error,
    createShift.error,
    updateShift.error,
    deactivateShift.error,
  );
  const busy =
    query.isFetching ||
    holidays.isFetching ||
    shifts.isFetching ||
    create.isPending ||
    reschedule.isPending ||
    cancel.isPending ||
    dueDate.isPending ||
    createHoliday.isPending ||
    updateHoliday.isPending ||
    deactivateHoliday.isPending ||
    createShift.isPending ||
    updateShift.isPending ||
    deactivateShift.isPending;
  return (
    <CmtsAdminCalendarSchedulerPage
      calendars={rows}
      holidays={dataOr(holidays.data, []) as readonly Record<string, unknown>[]}
      shifts={dataOr(shifts.data, []) as readonly Record<string, unknown>[]}
      masterOptions={{
        eventType: eventTypeOptions.data ?? [],
        priority: priorityOptions.data ?? [],
      }}
      diagnostics={diagnosticsOf(
        runtime.diagnostics,
        query.data,
        holidays.data,
        shifts.data,
        create.data,
        reschedule.data,
        cancel.data,
        dueDate.data,
      )}
      busy={busy}
      error={apiError}
      onCreateEvent={async (payload) => {
        await create.mutateAsync(payload);
      }}
      onRescheduleEvent={async (row, payload) => {
        await reschedule.mutateAsync({ row, payload });
      }}
      onCancelEvent={async (row) => {
        await cancel.mutateAsync(row);
      }}
      onTestDueDate={async (payload) => {
        await dueDate.mutateAsync(payload);
      }}
      onCreateHoliday={async (payload) => {
        await createHoliday.mutateAsync(payload);
      }}
      onUpdateHoliday={async (row, payload) => {
        await updateHoliday.mutateAsync({ row, payload });
      }}
      onDeactivateHoliday={async (row) => {
        await deactivateHoliday.mutateAsync(row);
      }}
      onCreateShift={async (payload) => {
        await createShift.mutateAsync(payload);
      }}
      onUpdateShift={async (row, payload) => {
        await updateShift.mutateAsync({ row, payload });
      }}
      onDeactivateShift={async (row) => {
        await deactivateShift.mutateAsync(row);
      }}
      onOpenLinkedRecord={(row) => {
        const id = String(row.complaintId ?? row.linkedEntityId ?? "");
        if (id) navigate(`/cmts/complaints/${encodeURIComponent(id)}/workflow`);
      }}
    />
  );
}

function firstUserSafeMessage(...errors: readonly unknown[]): string | null {
  for (const error of errors) {
    if (error === null || error === undefined) continue;
    return normalizeUserSafeApiError(error).userMessage;
  }
  return null;
}

export function CmtsMasterDependencyContainer({
  runtime,
}: CmtsConnectedContainerProps) {
  const t = tenantId(runtime);
  const [activeParams, setActiveParams] = useState({
    masterType: "CATEGORY",
    masterCode: "PRODUCT_QUALITY_COMPLAINT",
    operation: "READ_IMPACT",
  });

  const graph = useQuery({
    queryKey: ["cmts", "master-dependency", t, activeParams],
    queryFn: () =>
      runtime.repositories.masterDependencies.check({
        tenantId: t,
        masterType: activeParams.masterType,
        masterCode: activeParams.masterCode,
        operation: activeParams.operation,
        includeWarnings: true,
        includeRemediation: true,
        includeCircularDependencyCheck: true,
        includeRoutes: true,
        includeSchedules: true,
        includeReadiness: true,
      }),
    staleTime: 60_000,
    retry: false,
    throwOnError: false,
  });

  const graphData = graph.data?.ok ? (graph.data.data as any) : null;
  const rows = useMemo(() => {
    if (!graphData) return [];

    // Priority 1: Map real dependency edges (concrete record bindings)
    if (Array.isArray(graphData.edges) && graphData.edges.length > 0) {
      return graphData.edges.map((edge: any, idx: number) => {
        const isBlock = edge.blocking === true;
        const isWarn = edge.warning === true;
        const depEntity = String(edge.dependentEntity ?? "Binding");
        const depCode = String(
          edge.dependentRecordCode ??
            edge.dependentRecordId ??
            `REC-${idx + 1}`,
        );
        const rem = (graphData.remediations ?? []).find(
          (r: any) =>
            r.dependencyEdgeId === edge.edgeId ||
            r.actionCode?.includes(depEntity.toUpperCase()),
        );

        return {
          id: edge.edgeId ?? `edge-${idx + 1}`,
          code: depCode,
          policy: `${depEntity} Dependency (${edge.dependencyPath ?? depEntity})`,
          masterType: String(
            graphData.masterType ?? activeParams.masterType,
          ).toUpperCase(),
          appliesTo: String(edge.dependentModule ?? "workflow").toLowerCase(),
          sourceEntity: `${graphData.masterType ?? activeParams.masterType}: ${graphData.masterCode ?? activeParams.masterCode}`,
          targetEntity: `${depEntity}: ${depCode}`,
          result: isBlock ? "blocked" : isWarn ? "warn" : "allowed",
          block: isBlock ? "Yes" : "No",
          reason:
            edge.reason ??
            `${depEntity} record is bound to ${graphData.masterCode ?? activeParams.masterCode}.`,
          remediation:
            rem?.recommendedAction ??
            rem?.actionName ??
            `Review ${depEntity} bindings before deactivating or deleting master record.`,
          endpoint: "/api/cmts/master-dependencies/check",
          active: true,
          status: "Active",
        };
      });
    }

    // Priority 2: Fallback to impacts if edges are empty
    if (Array.isArray(graphData.impacts) && graphData.impacts.length > 0) {
      return graphData.impacts.map((imp: any, idx: number) => {
        const isBlock = imp.blocking === true;
        const isWarn = imp.warning === true;
        return {
          id: imp.impactId ?? `imp-${idx + 1}`,
          code: String(
            imp.recordCode ?? imp.entity ?? `IMP-${idx + 1}`,
          ).toUpperCase(),
          policy: `${imp.entity ?? "Impact"} Analysis`,
          masterType: String(
            graphData.masterType ?? activeParams.masterType,
          ).toUpperCase(),
          appliesTo: String(imp.module ?? "governance").toLowerCase(),
          sourceEntity: `${graphData.masterType ?? activeParams.masterType}: ${graphData.masterCode ?? activeParams.masterCode}`,
          targetEntity: `${imp.entity ?? "Module"}: ${imp.recordCode ?? "Linked System"}`,
          result: isBlock ? "blocked" : isWarn ? "warn" : "allowed",
          block: isBlock ? "Yes" : "No",
          reason: imp.reason ?? "Operational impact evaluated.",
          remediation: imp.recommendedAction ?? "Review linked dependencies.",
          endpoint: "/api/cmts/master-dependencies/check",
          active: true,
          status: "Active",
        };
      });
    }

    return [];
  }, [graphData, activeParams]);

  return (
    <CmtsMasterDependencyPage
      graph={graphData}
      rows={rows as readonly Record<string, unknown>[]}
      diagnostics={diagnosticsOf(runtime.diagnostics, graph.data)}
      onRefresh={async () => {
        await graph.refetch();
      }}
      onRunCheck={async (payload) => {
        const nextType = String(payload.masterType ?? "CATEGORY");
        const nextCode = String(
          payload.masterCode ?? "PRODUCT_QUALITY_COMPLAINT",
        );
        const nextOp = String(payload.operation ?? "READ_IMPACT");
        setActiveParams({
          masterType: nextType,
          masterCode: nextCode,
          operation: nextOp,
        });
      }}
      onRemediate={async (row) => {
        await runtime.repositories.masterDependencies.remediation({
          tenantId: t,
          ...row,
        });
        await graph.refetch();
      }}
    />
  );
}

export function CmtsScheduledJobsContainer({
  runtime,
}: CmtsConnectedContainerProps) {
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const dependencyData = useAdminDependencyData(runtime);
  const key = ["cmts", "admin", "scheduled-jobs", t];
  const query = useQuery({
    queryKey: key,
    queryFn: () => runtime.repositories.adminOperations.adminJobs(t),
    retry: 1,
  });
  const toRequest = (payload: Record<string, unknown>) => ({
    tenantId: t,
    jobCode: payload.jobCode ?? payload.code,
    jobName: payload.jobName ?? payload.label,
    jobType: payload.jobType,
    cronExpression: payload.cronExpression,
    maxRetryCount: Number(payload.maxRetryCount ?? 3),
    timeoutSeconds: Number(payload.timeoutSeconds ?? 600),
    isActive: payload.isActive ?? payload.active ?? true,
  });
  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      runtime.repositories.adminOperations.saveAdminJob(toRequest(payload)),
    onSuccess: async () => invalidateAdminArea(queryClient, t, [key]),
  });
  const deactivate = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.adminOperations.saveAdminJob(
        toRequest({ ...row, isActive: false }),
      ),
    onSuccess: async () => invalidateAdminArea(queryClient, t, [key]),
  });
  const reactivate = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.adminOperations.saveAdminJob(
        toRequest({ ...row, isActive: true }),
      ),
    onSuccess: async () => invalidateAdminArea(queryClient, t, [key]),
  });
  const runNow = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.adminOperations.runAdminJobNow(
        String(row.id ?? row.jobId ?? ""),
        { tenantId: t, reason: "Manual run from scheduled-job administration" },
      ),
    onSuccess: async () =>
      invalidateAdminArea(queryClient, t, [key, ["cmts", "dashboard", t]]),
  });
  return (
    <CmtsScheduledJobsPage
      rows={dataOr(query.data, []) as readonly Record<string, unknown>[]}
      diagnostics={diagnosticsOf(
        runtime.diagnostics,
        query.data,
        saveMutation.data,
        deactivate.data,
        reactivate.data,
        runNow.data,
      )}
      dependencyData={dependencyData}
      onUpsert={async (payload) => {
        assertRepositoryOk(
          await saveMutation.mutateAsync(payload),
          "Administration save",
        );
      }}
      onDeactivate={async (row) => {
        assertRepositoryOk(
          await deactivate.mutateAsync(row),
          "Administration deactivate",
        );
      }}
      onReactivate={async (row) => {
        assertRepositoryOk(
          await reactivate.mutateAsync(row),
          "Administration reactivate",
        );
      }}
      onTest={async (row) => {
        await runNow.mutateAsync(row);
      }}
      onRefresh={async () => {
        await query.refetch();
      }}
    />
  );
}

export function CmtsSodAdminContainer({
  runtime,
}: CmtsConnectedContainerProps) {
  const queryClient = useQueryClient();
  const t = tenantId(runtime);
  const dependencyData = useAdminDependencyData(runtime);
  const key = ["cmts", "reference", "sodPolicies", t];
  const referenceSodPolicies = useQuery({
    queryKey: key,
    queryFn: () => runtime.repositories.reference.sodPolicies(t),
    retry: 1,
  });

  const toUpsertRequest = (payload: Record<string, unknown>) => {
    const domain = String(payload.appliesTo ?? "approval").toLowerCase();
    let srcAction = String(payload.sourceActionCode ?? "")
      .trim()
      .toUpperCase();
    let restAction = String(payload.restrictedActionCode ?? "")
      .trim()
      .toUpperCase();
    if (!srcAction) {
      if (domain === "verification") srcAction = "INVESTIGATE";
      else if (domain === "configuration") srcAction = "DRAFT";
      else srcAction = "SUBMIT";
    }
    if (!restAction) {
      if (domain === "closure") restAction = "CLOSE";
      else if (domain === "verification") restAction = "VERIFY";
      else if (domain === "configuration") restAction = "ACTIVATE";
      else restAction = "APPROVE";
    }
    const isBlocked =
      payload.result === "blocked" || payload.sameUserRestricted === true;
    return {
      tenantId: t,
      id: payload.id ? String(payload.id) : undefined,
      code: String(payload.code ?? "")
        .trim()
        .toUpperCase(),
      name: String(payload.name ?? payload.policy ?? payload.code ?? "").trim(),
      description: payload.description
        ? String(payload.description).trim()
        : payload.reason
          ? String(payload.reason).trim()
          : null,
      sourceActionCode: srcAction,
      restrictedActionCode: restAction,
      sameUserRestricted: isBlocked,
      sameRoleRestricted: payload.sameRoleRestricted === true,
      sameDepartmentRestricted: payload.sameDepartmentRestricted === true,
      appliesToCategoryId: payload.appliesToCategoryId
        ? String(payload.appliesToCategoryId)
        : null,
      appliesToSeverityId: payload.appliesToSeverityId
        ? String(payload.appliesToSeverityId)
        : null,
      isActive:
        payload.isActive !== false &&
        payload.active !== false &&
        String(payload.status ?? "").toLowerCase() !== "inactive",
    };
  };

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      runtime.repositories.reference.saveSodPolicy(toUpsertRequest(payload), t),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
      await referenceSodPolicies.refetch();
    },
  });

  const deactivate = useMutation({
    mutationFn: (row: Record<string, unknown>) => {
      const id = String(row.id ?? "");
      if (id && id.length === 36) {
        return runtime.repositories.reference.deactivateSodPolicy(id, t);
      }
      return runtime.repositories.reference.saveSodPolicy(
        toUpsertRequest({ ...row, active: false, isActive: false }),
        t,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
      await referenceSodPolicies.refetch();
    },
  });

  const reactivate = useMutation({
    mutationFn: (row: Record<string, unknown>) => {
      const id = String(row.id ?? "");
      if (id && id.length === 36) {
        return runtime.repositories.reference.reactivateSodPolicy(id, t);
      }
      return runtime.repositories.reference.saveSodPolicy(
        toUpsertRequest({ ...row, active: true, isActive: true }),
        t,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
      await referenceSodPolicies.refetch();
    },
  });

  const test = useMutation({
    mutationFn: (row: Record<string, unknown>) =>
      runtime.repositories.reference.testSodPolicy({
        ...row,
        tenantId: t,
        testActionCode:
          row.appliesToWorkflowAction ?? row.actionCode ?? "APPROVE_CLOSURE",
        actionCode:
          row.appliesToWorkflowAction ?? row.actionCode ?? "APPROVE_CLOSURE",
        actorId: readCmtsSession()?.userId ?? "user-creator",
        createdBy: row.createdBy ?? readCmtsSession()?.userId ?? "user-creator",
        ownerId:
          row.ownerId ??
          row.createdBy ??
          readCmtsSession()?.userId ??
          "user-creator",
        actorRoles: readCmtsSession()?.roles ?? [],
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: key });
    },
  });

  const sodRows = (
    dataOr(referenceSodPolicies.data, []) as readonly Record<string, unknown>[]
  ).map((row) => {
    const src = String(row.sourceActionCode ?? "").trim();
    const rest = String(row.restrictedActionCode ?? "").trim();
    const isBlocked =
      row.sameUserRestricted === true ||
      row.sameRoleRestricted === true ||
      row.sameDepartmentRestricted === true;
    const isActive = row.isActive !== false;
    let appliesTo = "approval";
    if (rest === "CLOSE") appliesTo = "closure";
    else if (rest === "VERIFY") appliesTo = "verification";
    else if (rest === "ACTIVATE") appliesTo = "configuration";
    else if (rest === "RCA") appliesTo = "rca";

    return {
      ...row,
      code: row.code,
      name: row.name,
      policy: row.name ?? row.policyName ?? row.code,
      description: row.description ?? row.reason,
      reason:
        row.description ??
        row.reason ??
        "Segregation of duties rule violation.",
      sourceActionCode: src,
      restrictedActionCode: rest,
      appliesTo,
      domain: appliesTo,
      result: isBlocked ? "blocked" : "warn",
      enforcementResult: isBlocked ? "blocked" : "warn",
      initialRole: src || "Author / Submitter",
      restrictedAction: rest || "Approve / Close",
      active: isActive,
      status: isActive ? "Active" : "Inactive",
    };
  });

  return (
    <CmtsSodAdminPage
      rows={referenceSodPolicies.data ? sodRows : undefined}
      loading={referenceSodPolicies.isLoading}
      diagnostics={diagnosticsOf(
        runtime.diagnostics,
        referenceSodPolicies.data,
        saveMutation.data,
        deactivate.data,
        reactivate.data,
        test.data,
      )}
      dependencyData={dependencyData}
      onUpsert={async (payload) => {
        assertRepositoryOk(
          await saveMutation.mutateAsync(payload),
          "Administration save",
        );
      }}
      onDeactivate={async (row) => {
        assertRepositoryOk(
          await deactivate.mutateAsync(row),
          "Administration deactivate",
        );
      }}
      onReactivate={async (row) => {
        assertRepositoryOk(
          await reactivate.mutateAsync(row),
          "Administration reactivate",
        );
      }}
      onTest={async (row) => {
        await test.mutateAsync(row);
      }}
      onRefresh={async () => {
        await referenceSodPolicies.refetch();
      }}
    />
  );
}
